import React, { useState, useEffect, useRef } from 'react';
import './product_audit.css';
import Trial from '../Trial';
import { Link } from 'react-router-dom';

const releases = [
  {
    version: 'v4.2',
    date: 'April 2025',
    badge: 'Latest',
    color: '#667eea',
    highlights: ['Feature Master for Dynamic Voucher Entry', 'Live Row-by-Row Data Sync', 'Ctrl+Q Smart Query Window'],
  },
  {
    version: 'v4.1',
    date: 'January 2025',
    badge: 'Stable',
    color: '#43e97b',
    highlights: ['Unified Invoice for Goods & Services', 'Barcode Read & Custom Design', 'Drill-Down Reporting from Vouchers'],
  },
  {
    version: 'v4.0',
    date: 'October 2024',
    badge: 'Major',
    color: '#f093fb',
    highlights: ['Customizable Invoice Design', 'Tailored Report Format Designer', 'Multi-Currency Support'],
  },
];

const features = [
  { icon: '⚙️', gradient: 'linear-gradient(135deg,#667eea,#764ba2)', title: 'Feature Master for Dynamic Voucher Entry', description: 'Custom fields in vouchers adapt automatically based on whether the transaction is for accounts, inventory, or services—streamlining data entry and accuracy.' },
  { icon: '🧾', gradient: 'linear-gradient(135deg,#f093fb,#f5576c)', title: 'Unified Invoice for Goods & Services', description: 'Create a single invoice that seamlessly includes both goods and service items, simplifying billing and reducing errors.' },
  { icon: '🔄', gradient: 'linear-gradient(135deg,#4facfe,#00f2fe)', title: 'Live Row-by-Row Data Sync', description: 'Real-time updates during voucher entry ensure immediate reflection of stock levels and account balances, even in multi-user environments.' },
  { icon: '⌨️', gradient: 'linear-gradient(135deg,#43e97b,#38f9d7)', title: 'Ctrl+Q Smart Query Window', description: 'Access all search and query operations using just one shortcut key—Ctrl+Q—eliminating the need to remember multiple key combinations.' },
  { icon: '📈', gradient: 'linear-gradient(135deg,#fa709a,#fee140)', title: 'Drill-Down Reporting from Vouchers', description: 'Easily drill down from summary reports to specific voucher entries, enabling faster audit and verification.' },
  { icon: '🔳', gradient: 'linear-gradient(135deg,#a18cd1,#fbc2eb)', title: 'Barcode Read & Custom Design', description: 'Supports EAN barcode scanning and lets users design their own barcode layouts for both reading and printing.' },
  { icon: '📄', gradient: 'linear-gradient(135deg,#ffecd2,#fcb69f)', title: 'Customizable Invoice Design', description: 'Design and format invoices to match branding and business needs with flexible layout tools.' },
  { icon: '📋', gradient: 'linear-gradient(135deg,#a1c4fd,#c2e9fb)', title: 'Tailored Report Format Designer', description: 'Create business-specific reports with fully customizable templates for printing, exporting, or analysis.' },
  { icon: '💱', gradient: 'linear-gradient(135deg,#d4fc79,#96e6a1)', title: 'Multi-Currency Support', description: 'Handle transactions in multiple currencies, with automatic exchange rate updates, ensuring global business operations are smooth.' },
  { icon: '🛡️', gradient: 'linear-gradient(135deg,#f6d365,#fda085)', title: 'Role-Based Access Control', description: 'Assign user permissions based on roles within the organization, ensuring data security and restricted access to sensitive information.' },
  { icon: '🧮', gradient: 'linear-gradient(135deg,#89f7fe,#66a6ff)', title: 'Automatic Tax Calculation', description: 'Automatically calculate taxes based on user-defined tax rates, jurisdictions, and product categories.' },
  { icon: '📦', gradient: 'linear-gradient(135deg,#fddb92,#d1fdff)', title: 'Sales & Purchase Order Management', description: 'Create and manage sales and purchase orders with integrated stock and financial tracking.' },
  { icon: '🏦', gradient: 'linear-gradient(135deg,#e0c3fc,#8ec5fc)', title: 'Integration with Banking Systems', description: 'Seamlessly integrate with banking systems to facilitate real-time reconciliation of bank statements and payments.' },
  { icon: '📊', gradient: 'linear-gradient(135deg,#f5f7fa,#c3cfe2)', title: 'Customizable Dashboards', description: 'Personalize dashboards for each user to display key metrics, financial data, and reports for real-time insights.' },
  { icon: '🔍', gradient: 'linear-gradient(135deg,#667eea,#764ba2)', title: 'Audit Trail for Data Integrity', description: 'Keep a complete, unalterable record of all system changes, ensuring compliance and providing a reliable audit trail.' },
  { icon: '☁️', gradient: 'linear-gradient(135deg,#4facfe,#00f2fe)', title: 'Cloud and On-Premises Deployment', description: 'Offer flexibility with both cloud-based and on-premises deployment options, giving businesses control over their environment.' },
];

const reportDesign = [
  { title: 'Drag-and-Drop Interface', content: 'Intuitive drag-and-drop interface for effortless report design, making it easy to arrange and organize elements on the report canvas.' },
  { title: 'Data Source Integration', content: 'Connect seamlessly to various data sources, including databases and spreadsheets, to pull in and analyze real-time data.' },
  { title: 'Parameterized Reports', content: 'Create dynamic reports with parameters, allowing users to customize data filters and criteria to meet their specific reporting needs.' },
  { title: 'Formatting Options', content: 'Full control over fonts, colors, and styles to ensure that your reports align with your organization\'s branding.' },
  { title: 'Chart and Graph Integration', content: 'Incorporate a variety of charts and graphs to visually represent data trends and insights.' },
  { title: 'Export and Sharing', content: 'Effortlessly export reports in multiple formats (PDF, Excel, CSV) and share them with stakeholders.' },
  { title: 'Conditional Formatting', content: 'Apply formatting rules based on specified conditions to enhance the visual representation of data.' },
  { title: 'Calculation and Formula Support', content: 'Include calculated fields and formulas within the report for performing computations on the data.' },
  { title: 'Grouping and Sorting', content: 'Organize data effectively with options for grouping and sorting, ensuring a logical presentation.' },
  { title: 'Templates', content: 'Save and reuse report templates for consistency across different reports, streamlining the report creation process.' },
];

const dashboardDesign = [
  { title: 'Customizable Solutions', content: 'Tailor Auditus-IERP to fit your startup\'s unique needs with solutions that adapt to your business requirements.' },
  { title: 'Scalability', content: 'As your startup grows, Auditus-IERP scales with you to remain a reliable and efficient solution.' },
  { title: 'Collaborative Tools', content: 'Enhance teamwork with secure financial data sharing and streamlined communication features.' },
  { title: 'Customizable Dashboards', content: 'Design personalized dashboards to monitor key metrics and insights important to your startup.' },
  { title: 'Drag-and-Drop Widget Placement', content: 'Easily arrange widgets on dashboards using an intuitive drag-and-drop interface.' },
  { title: 'Real-time Data Integration', content: 'Seamlessly integrate real-time data from multiple sources for up-to-date decision-making insights.' },
  { title: 'Data Visualization Widgets', content: 'Use charts, graphs, and gauges to transform complex data into easily understandable visuals.' },
  { title: 'Performance Metrics Monitoring', content: 'Track critical KPIs and metrics to evaluate and improve your startup\'s performance.' },
  { title: 'Interactive Drill-Down Functionality', content: 'Explore specific data points in detail directly from the dashboard for deeper analysis.' },
  { title: 'Customizable Time Periods', content: 'Adjust dashboards to show historical or real-time data over selectable time ranges.' },
];

export default function Whatsnew() {
  const [activeReport, setActiveReport] = useState(null);
  const [activeDash, setActiveDash] = useState(null);
  const [activeRelease, setActiveRelease] = useState(0);

  return (
    <div className="wn-root">
      {/* ── HERO ── */}
      <section className="wn-hero">
        <div className="wn-hero-orb wn-orb-1" />
        <div className="wn-hero-orb wn-orb-2" />
        <div className="wn-hero-orb wn-orb-3" />
        <div className="wn-hero-inner">
          <div className="wn-hero-badge">
            <span className="wn-badge-dot" />
            Latest Release — v4.2 April 2025
          </div>
          <h1 className="wn-hero-title">
            Experience the <span className="wn-hero-gradient">Future of ERP</span>
            <br />with the All-New Auditusierp
          </h1>
          <p className="wn-hero-sub">
            Discover a faster, smarter, and more intuitive way to manage your business — with
            powerful features, improved performance, and a refreshed user experience.
          </p>
          <div className="wn-hero-actions">
            <Link to="/Demo" className="wn-btn-primary">
              <span>Try for Free</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
            </Link>
            <Link to="/Demo" className="wn-btn-ghost">Get a Demo</Link>
          </div>
          <div className="wn-hero-img-wrap">
            <div className="wn-hero-glow" />
            <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=900&q=80" alt="Auditusierp what's new" className="wn-hero-img" />
          </div>
        </div>
      </section>

      {/* ── RELEASE TIMELINE ── */}
      <section className="wn-releases">
        <div className="wn-section-header">
          <span className="wn-section-tag">Release Notes</span>
          <h2 className="wn-section-title">Version History</h2>
          <p className="wn-section-sub">What we shipped and when</p>
        </div>
        <div className="wn-release-tabs">
          {releases.map((r, i) => (
            <button
              key={i}
              className={`wn-release-tab ${activeRelease === i ? 'active' : ''}`}
              onClick={() => setActiveRelease(i)}
              style={{ '--tab-color': r.color }}
            >
              <span className="wn-release-tab-ver">{r.version}</span>
              <span className="wn-release-tab-date">{r.date}</span>
              {r.badge === 'Latest' && <span className="wn-release-badge">Latest</span>}
            </button>
          ))}
        </div>
        <div className="wn-release-panel">
          <div className="wn-release-panel-top" style={{ background: releases[activeRelease].color }}>
            <span className="wn-release-panel-ver">{releases[activeRelease].version}</span>
            <span className="wn-release-panel-label">Release Highlights</span>
          </div>
          <ul className="wn-release-list">
            {releases[activeRelease].highlights.map((h, i) => (
              <li key={i} className="wn-release-li">
                <span className="wn-release-check" style={{ color: releases[activeRelease].color }}>✓</span>
                {h}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── FEATURES GRID ── */}
      <section className="wn-features">
        <div className="wn-section-header">
          <span className="wn-section-tag">What's New</span>
          <h2 className="wn-section-title">All New Features in Auditusierp</h2>
          <p className="wn-section-sub">16 powerful additions designed for smarter business management</p>
        </div>
        <div className="wn-feat-grid">
          {features.map((f, i) => (
            <FeatureCard key={i} f={f} index={i} />
          ))}
        </div>
      </section>

      {/* ── DESIGNER SECTIONS ── */}
      <section className="wn-designers">
        <div className="wn-section-header">
          <span className="wn-section-tag">Built-In Tools</span>
          <h2 className="wn-section-title">Powerful Designer Modules</h2>
          <p className="wn-section-sub">Report and dashboard creation right inside your ERP</p>
        </div>
        <div className="wn-designer-grid">
          {/* Report Designer */}
          <div className="wn-designer-card">
            <div className="wn-designer-head">
              <div className="wn-designer-icon" style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)' }}>📊</div>
              <div>
                <h3 className="wn-designer-title">Report Designer</h3>
                <p className="wn-designer-sub">Revolutionize your reporting experience with Auditus-IERP's advanced Report Designer Tool.</p>
              </div>
            </div>
            <div className="wn-acc-list">
              {reportDesign.map((item, index) => (
                <div key={index} className={`wn-acc-item ${activeReport === index ? 'open' : ''}`}>
                  <button className="wn-acc-q" onClick={() => setActiveReport(activeReport === index ? null : index)}>
                    <span>{item.title}</span>
                    <span className="wn-acc-chevron">{activeReport === index ? '−' : '+'}</span>
                  </button>
                  <div className="wn-acc-a"><p>{item.content}</p></div>
                </div>
              ))}
            </div>
          </div>

          {/* Dashboard Designer */}
          <div className="wn-designer-card">
            <div className="wn-designer-head">
              <div className="wn-designer-icon" style={{ background: 'linear-gradient(135deg,#43e97b,#38f9d7)' }}>🖥️</div>
              <div>
                <h3 className="wn-designer-title">Dashboard Designer</h3>
                <p className="wn-designer-sub">Empower decision-making with Auditus-IERP's Dashboard Designer for at-a-glance insights.</p>
              </div>
            </div>
            <div className="wn-acc-list">
              {dashboardDesign.map((item, index) => (
                <div key={index} className={`wn-acc-item ${activeDash === index ? 'open' : ''}`}>
                  <button className="wn-acc-q" onClick={() => setActiveDash(activeDash === index ? null : index)}>
                    <span>{item.title}</span>
                    <span className="wn-acc-chevron">{activeDash === index ? '−' : '+'}</span>
                  </button>
                  <div className="wn-acc-a"><p>{item.content}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Trial />
    </div>
  );
}

function FeatureCard({ f, index }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`wn-feat-card ${visible ? 'wn-feat-card--visible' : ''}`}
      style={{ transitionDelay: `${(index % 4) * 80}ms` }}
    >
      <div className="wn-feat-icon-wrap" style={{ background: f.gradient }}>
        <span className="wn-feat-icon">{f.icon}</span>
      </div>
      <div className="wn-feat-line" style={{ background: f.gradient }} />
      <h3 className="wn-feat-title">{f.title}</h3>
      <p className="wn-feat-desc">{f.description}</p>
    </div>
  );
}
